
import { useEffect, useRef, useState } from 'react';
import { HeartHandshake, MessageSquareText, ReceiptText, Repeat, WalletCards } from 'lucide-react';
import SelectWhatsappModal from '../../SelectWhatsappModal';

const defaultScenarios = [
  {
    title: 'A Peer With Time',
    calls: 'For loneliness, encouragement, or ordinary life stories',
    rate: 'Good for: friendly check-ins',
    monthly: 'Listen',
    description: 'A digital friend who can ask good questions, remember details, and make someone feel like their day matters.',
  },
  {
    title: 'An Elder or Mentor',
    calls: 'For work, family, mistakes, transitions, and decisions',
    rate: 'Good for: lived counsel',
    monthly: 'Share',
    description: "Someone who has lived through a few chapters and can offer perspective without pretending to have every answer.",
    featured: true
  },
  {
    title: 'A Cultural Companion',
    calls: 'For learning about other countries, faiths, food, work, and daily life',
    rate: 'Good for: widening perspective',
    monthly: 'Learn',
    description: 'A person from somewhere else who can trade stories, explain what life feels like there, and search for shared humanity.',
  },
];

const exampleOffers = [
  {
    icon: MessageSquareText,
    title: 'Weekly Texts',
    price: '$10-25 / week per customer',
    description: 'A guide might offer a steady weekly check-in, reflection prompt, prayer, encouragement note, or thoughtful reply thread.',
  },
  {
    icon: ReceiptText,
    title: 'One-Time Conversation',
    price: '$15-50 per customer',
    description: 'After a good introduction, a guide can send an invoice for a longer WhatsApp conversation, voice note exchange, or life-counsel session.',
  },
  {
    icon: Repeat,
    title: 'Ongoing Support',
    price: '$40-150 / month per customer',
    description: 'For people who want continuity, guides can suggest recurring support for weekly texts, monthly calls, or a simple mentoring rhythm.',
  },
];

const paymentSteps = [
  'Create a virtual product',
  'Choose a suggested donation',
  'Send an invoice',
  'Collect through The Boggler',
];

export default function ConversationPaths({ scenarios: scenariosProp, onApply }) {
  const scenarios = scenariosProp || defaultScenarios;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <SelectWhatsappModal show={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <section ref={sectionRef} className="py-20 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl text-dark-teal text-center mb-4">
            Conversations Worth Having
          </h2>
          <p className="text-xl text-secondary-text text-center mb-16 font-lato max-w-3xl mx-auto">
            The Boggler is a virtual pen-pal platform for one-on-one conversations with donation-based guides around the world.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {scenarios.map((scenario, index) => (
              <div
                key={index}
                className={`bg-white rounded-boggler p-8 shadow-card hover:shadow-boggler transition-all duration-300 scroll-animate ${
                  scenario.featured ? 'border-4 border-orange-cta transform md:scale-105' : ''
                } ${isVisible ? 'visible' : ''}`}
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {scenario.featured && (
                  <div className="bg-orange-cta text-white text-sm font-montserrat font-semibold px-4 py-1 rounded-full inline-block mb-4">
                    Often Needed
                  </div>
                )}
                <h3 className="text-2xl text-heading-dark mb-3">{scenario.title}</h3>
                <div className="mb-4">
                  <div className="text-muted-text font-lato mb-1">{scenario.calls}</div>
                  <div className="text-muted-text font-lato mb-1">{scenario.rate}</div>
                  <div className="text-5xl text-golden-yellow font-lucky mt-3">{scenario.monthly}</div>
                  <div className="text-muted-text font-lato text-sm">one person at a time</div>
                </div>
                <p className="text-primary-text font-lato text-sm">{scenario.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-warm-cream rounded-boggler p-8 text-center">
            <h3 className="lucky-font text-2xl text-heading-dark mb-3">Donation Based, Connection First</h3>
            <p className="text-primary-text font-lato leading-relaxed max-w-3xl mx-auto mb-6">
              Guides can receive support from people who value their time, but The Boggler is not built around turning care into a hard sell.
              It is built around respectful introductions, private conversations, and the simple relief of being heard. That being said, there are avenues to monetize your work.
              Through the guides platform, you can create virtual products like weekly texts, one-time conversations, or recurring support, then send an invoice with a suggested donation price of your choosing.
              Guides can make a substantial income guiding on The Boggler, depending on who they connect with and that person&apos;s willingness to offer financial support.
              It is hard to say how much you will make for sure, but The Boggler gives you tools to ask for and collect payments according to your own standards.
            </p>

            <div className="grid md:grid-cols-3 gap-5 mb-8 text-left">
              {exampleOffers.map((offer) => {
                const OfferIcon = offer.icon;

                return (
                  <div key={offer.title} className="bg-white rounded-boggler p-6 shadow-card border border-white">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="w-12 h-12 rounded-full bg-light-warm flex items-center justify-center text-boggler-teal">
                        <OfferIcon className="w-6 h-6" aria-hidden="true" />
                      </div>
                      <div className="text-right">
                        <div className="text-xs uppercase font-montserrat text-muted-text">example</div>
                        <div className="text-xl font-lucky text-golden-yellow">{offer.price}</div>
                      </div>
                    </div>
                    <h4 className="text-xl text-heading-dark font-montserrat font-semibold mb-2">{offer.title}</h4>
                    <p className="text-sm text-primary-text font-lato leading-relaxed">{offer.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="bg-white rounded-boggler p-5 mb-8 shadow-card">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center justify-center gap-3 text-heading-dark font-montserrat font-semibold md:w-56">
                  <WalletCards className="w-6 h-6 text-boggler-teal" aria-hidden="true" />
                  How payment works
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
                  {paymentSteps.map((step, index) => (
                    <div key={step} className="flex items-center gap-3 bg-light-warm rounded-lg px-4 py-3">
                      <span className="w-7 h-7 rounded-full bg-boggler-teal text-white flex items-center justify-center font-montserrat font-semibold text-sm">
                        {index + 1}
                      </span>
                      <span className="text-sm text-primary-text font-lato">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-orange-cta text-white px-8 py-4 rounded-full font-montserrat font-semibold shadow-card btn-hover-lift inline-flex items-center gap-2"
            >
              <HeartHandshake className="w-6 h-6" aria-hidden="true" />
              Offer an Ear
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
